import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  Released: '#46BB89',
  Draft: '#94a3b8',
  'In Review': '#60a5fa',
  Superseded: '#f59e0b',
  Obsolete: '#EC2124',
  Archived: '#64748b',
};

const LEVEL_COLORS = {
  MASTER: '#a78bfa',
  FAMILY: '#60a5fa',
  SPECIFIC: '#46BB89',
};

export default function QualityDocumentTree() {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [expandAll, setExpandAll] = useState(null); // null=default, true=all, false=collapsed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/quality/documents/tree`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.ok) setTree(d.tree || []);
      else setError(d.message || 'Failed to load tree');
    } catch (e) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  // Collect filter options from the flat set of all nodes
  const allNodes = useMemo(() => {
    const flat = [];
    const walk = (nodes) => nodes.forEach(n => { flat.push(n); walk(n.children || []); });
    walk(tree);
    return flat;
  }, [tree]);

  const families = useMemo(() =>
    [...new Set(allNodes.map(n => n.productFamily).filter(Boolean))].sort()
  , [allNodes]);

  const suppliers = useMemo(() =>
    [...new Set(allNodes.flatMap(n => n.suppliers || []))].sort()
  , [allNodes]);

  // Filter: keep a node if it or any descendant matches
  const nodeMatchesFilter = useCallback((node) => {
    if (filterStatus && node.currentStatus !== filterStatus) return false;
    if (filterFamily && node.productFamily !== filterFamily) return false;
    if (filterSupplier && !(node.suppliers || []).includes(filterSupplier)) return false;
    return true;
  }, [filterStatus, filterFamily, filterSupplier]);

  const hasActiveFilter = filterStatus || filterFamily || filterSupplier;

  // Filter tree: keep a node if it passes or has passing descendants
  const filterTree = useCallback((nodes) => {
    return nodes.reduce((acc, node) => {
      const filteredChildren = filterTree(node.children || []);
      if (nodeMatchesFilter(node) || filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
      return acc;
    }, []);
  }, [nodeMatchesFilter]);

  const displayTree = useMemo(() =>
    hasActiveFilter ? filterTree(tree) : tree
  , [tree, hasActiveFilter, filterTree]);

  if (loading) return <div style={{ color: 'var(--ni-text-muted)', padding: 40 }}>Loading tree…</div>;
  if (error) return <div style={{ color: '#EC2124', padding: 40 }}>{error}</div>;

  const totalVisible = allNodes.length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Document Hierarchy</h2>
        <div style={{ fontSize: 13, color: 'var(--ni-text-muted)' }}>
          {totalVisible} document{totalVisible !== 1 ? 's' : ''} · MASTER → FAMILY → SPECIFIC
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        background: 'var(--ni-panel)',
        border: '1px solid var(--ni-border-soft)',
        borderRadius: 10,
        padding: '14px 18px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', flexShrink: 0 }}>Filter</span>

        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={selectStyle}
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select
          value={filterFamily}
          onChange={e => setFilterFamily(e.target.value)}
          style={selectStyle}
          disabled={families.length === 0}
        >
          <option value="">All families</option>
          {families.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <select
          value={filterSupplier}
          onChange={e => setFilterSupplier(e.target.value)}
          style={selectStyle}
          disabled={suppliers.length === 0}
        >
          <option value="">All suppliers</option>
          {suppliers.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>

        {hasActiveFilter && (
          <button
            onClick={() => { setFilterStatus(''); setFilterFamily(''); setFilterSupplier(''); }}
            style={{ background: 'none', border: 'none', color: '#EC2124', fontSize: 12, cursor: 'pointer', padding: '4px 8px' }}
          >
            Clear filters
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => setExpandAll(true)} style={smallBtnStyle}>Expand all</button>
          <button onClick={() => setExpandAll(false)} style={smallBtnStyle}>Collapse all</button>
        </div>
      </div>

      {/* Tree */}
      <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
        {displayTree.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--ni-text-muted)', fontSize: 13 }}>
            {hasActiveFilter ? 'No documents match the current filters.' : 'No documents yet.'}
          </div>
        ) : (
          displayTree.map((node, i) => (
            <DocumentNode
              key={node._id}
              node={node}
              depth={0}
              navigate={navigate}
              isLast={i === displayTree.length - 1}
              expandAll={expandAll}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DocumentNode({ node, depth, navigate, isLast, expandAll }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = (node.children || []).length > 0;

  // Respond to global expand/collapse
  useEffect(() => {
    if (expandAll === true) setExpanded(true);
    if (expandAll === false) setExpanded(false);
  }, [expandAll]);

  const statusColor = STATUS_COLORS[node.currentStatus] || '#666';
  const levelColor = LEVEL_COLORS[node.level] || '#666';
  const indent = depth * 28;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '9px 18px',
          paddingLeft: 18 + indent,
          borderBottom: isLast && !expanded ? 'none' : '1px solid var(--ni-border-soft)',
          cursor: 'pointer',
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {/* Expand/collapse toggle */}
        <div
          onClick={e => { e.stopPropagation(); if (hasChildren) setExpanded(x => !x); }}
          style={{
            width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--ni-text-muted)', fontSize: 10, cursor: hasChildren ? 'pointer' : 'default',
          }}
        >
          {hasChildren ? (expanded ? '▼' : '▶') : '·'}
        </div>

        {/* Level badge */}
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: levelColor, background: levelColor + '18',
          padding: '2px 7px', borderRadius: 4, flexShrink: 0, minWidth: 56, textAlign: 'center',
        }}>
          {node.level}
        </span>

        {/* Doc number */}
        <span
          onClick={() => navigate(`/quality/documents/${node._id}`)}
          style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ni-green)', fontWeight: 700, flexShrink: 0, cursor: 'pointer' }}
        >
          {node.documentNumber}
        </span>

        {/* Title */}
        <span
          onClick={() => navigate(`/quality/documents/${node._id}`)}
          style={{ fontSize: 13, color: 'var(--ni-text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
        >
          {node.title}
        </span>

        {/* Meta chips */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {node.productFamily && (
            <span style={{ fontSize: 10, color: 'var(--ni-text-muted)', background: 'rgba(255,255,255,0.05)', padding: '1px 7px', borderRadius: 4 }}>
              {node.productFamily}
            </span>
          )}
          {node.currentRevision && (
            <span style={{ fontSize: 10, color: 'var(--ni-text-muted)' }}>Rev. {node.currentRevision}</span>
          )}
          {(node.children || []).length > 0 && (
            <span style={{ fontSize: 10, color: 'var(--ni-text-muted)' }}>
              {node.children.length} child{node.children.length !== 1 ? 'ren' : ''}
            </span>
          )}
          {/* Status dot */}
          <span style={{
            fontSize: 10, fontWeight: 700, color: statusColor,
            background: statusColor + '18', padding: '2px 8px', borderRadius: 10,
          }}>
            {node.currentStatus}
          </span>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div style={{ borderLeft: `2px solid ${levelColor}22`, marginLeft: 18 + indent + 9 }}>
          {node.children.map((child, i) => (
            <DocumentNode
              key={child._id}
              node={child}
              depth={depth + 1}
              navigate={navigate}
              isLast={i === node.children.length - 1}
              expandAll={expandAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  background: 'var(--ni-panel-raised, #1D2522)',
  border: '1px solid var(--ni-border-soft)',
  color: 'var(--ni-text)',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 12,
  cursor: 'pointer',
};

const smallBtnStyle = {
  background: 'none',
  border: '1px solid var(--ni-border-soft)',
  color: 'var(--ni-text-muted)',
  borderRadius: 6,
  padding: '5px 12px',
  fontSize: 12,
  cursor: 'pointer',
};
